import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { getOpenBets, getPlatformStats } from '../lib/api';
import { BetCard } from '../components/bets/BetCard';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { 
    ArrowRight,
    Shield,
    Globe,
    Lock,
    Coins,
    Wallet,
    CheckCircle,
    Crown,
    Users,
    Flame,
    Zap
} from 'lucide-react';
import { TokenomicsWidget } from '../components/TokenomicsWidget';
import RoiCalculator from '../components/RoiCalculator';

const BET4PEER_LOGO = "https://customer-assets.emergentagent.com/job_83ddb6ac-1a43-447f-9a3b-123d64b2d59d/artifacts/pter9nuv_Logo%20futuriste%20Bet4Peer%20avec%20poigne%CC%81e%20de%20main.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function HomePage() {
    const { address, isConnected, connect } = useWeb3();
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [featuredBets, setFeaturedBets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sponsorStats, setSponsorStats] = useState(null);

    const features = [
        { icon: Shield, title: t('home.feat1'), description: t('home.feat1Desc') },
        { icon: Globe,  title: t('home.feat2'), description: t('home.feat2Desc') },
        { icon: Lock,   title: t('home.feat3'), description: t('home.feat3Desc') },
        { icon: Coins,  title: t('home.feat4'), description: t('home.feat4Desc') },
    ];

    const steps = [
        { number: '01', title: t('home.step1'), description: t('home.step1Desc') },
        { number: '02', title: t('home.step2'), description: t('home.step2Desc') },
        { number: '03', title: t('home.step3'), description: t('home.step3Desc') },
        { number: '04', title: t('home.step4'), description: t('home.step4Desc') },
    ];

    useEffect(() => {
        async function fetchData() {
            try {
                const [statsData, betsData] = await Promise.all([
                    getPlatformStats(),
                    getOpenBets({ limit: 3 }),
                ]);
                setStats(statsData);
                setFeaturedBets(betsData);
                // Fetch sponsor stats (best-effort, non-critical)
                try {
                    const res = await fetch(`${API_URL}/api/sponsor/stats`);
                    if (res.ok) setSponsorStats(await res.json());
                } catch (sponsorErr) {
                    console.warn('Sponsor stats unavailable:', sponsorErr?.message);
                }
            } catch (error) {
                console.error('Failed to fetch home data:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    return (
        <div data-testid="home-page" className="page-enter" role="article" aria-label="Bet4Peer - Decentralized P2P Betting">
            {/* Hero Section */}
            <section className="relative overflow-hidden min-h-[80vh] flex items-center">
                {/* Background gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#00FF66]/5 via-transparent to-transparent" />
                
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Left - Text Content */}
                        <div>
                            {/* Badge */}
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FF66]/10 border border-[#00FF66]/20 mb-6 animate-fade-in-down delay-0">
                                <div className="w-2 h-2 rounded-full bg-[#00FF66] animate-pulse" />
                                <span className="text-[#00FF66] text-sm font-medium">{t('home.badge')}</span>
                            </div>

                            {/* B4P launch teaser */}
                            <Link
                                to="/b4p-launch"
                                className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 ml-3 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] text-xs font-bold uppercase tracking-wider hover:bg-[#FFD700]/15 transition-colors"
                                data-testid="hero-b4p-teaser"
                            >
                                <Crown className="w-3 h-3" />
                                B4P LAUNCH · {t('home.tierStarter')} → {t('home.tierElite')}
                                <ArrowRight className="w-3 h-3" />
                            </Link>

                            {/* Headline */}
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 animate-fade-in-up delay-1">
                                <span className="font-serif-luxury text-white block tracking-tight">{t('home.heroTitle1')}</span>
                                <span className="font-serif-luxury-italic text-gold-gradient block">{t('home.heroTitle2')}</span>
                            </h1>

                            <p className="text-lg text-white/60 mb-8 max-w-lg leading-relaxed animate-fade-in-up delay-2">
                                {t('home.heroDesc')}
                                <span className="text-[#00FF66]"> {t('home.heroAccent')}</span>
                            </p>

                            {/* CTAs */}
                            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-3">
                                <Link to="/create">
                                    <Button 
                                        size="lg"
                                        className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] hover:shadow-[0_0_30px_rgba(130,255,122,0.4)] font-semibold px-8 py-6 text-lg w-full sm:w-auto btn-press"
                                        data-testid="hero-create-bet-btn"
                                        aria-label="Create a new P2P bet"
                                    >
                                        {t('home.createBet')}
                                    </Button>
                                </Link>
                                <Link to="/bets">
                                    <Button 
                                        variant="outline" 
                                        size="lg"
                                        className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-lg w-full sm:w-auto btn-press"
                                        data-testid="hero-browse-bets-btn"
                                        aria-label="Browse all open bets"
                                    >
                                        {t('home.browseBets')}
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                            </div>

                            {/* Quick Stats inline */}
                            <div className="flex flex-wrap items-center gap-4 sm:gap-8 mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-white/10 animate-fade-in delay-5">
                                <div>
                                    <div className="font-mono text-xl sm:text-2xl font-bold text-white stat-number delay-6">{stats?.total_bets || 0}</div>
                                    <div className="text-white/40 text-xs sm:text-sm">{t('home.totalBets')}</div>
                                </div>
                                <div>
                                    <div className="font-mono text-xl sm:text-2xl font-bold text-[#00FF66] stat-number delay-7">{(stats?.total_volume_eth || 0).toFixed(2)}</div>
                                    <div className="text-white/40 text-xs sm:text-sm">{t('home.ethVolume')}</div>
                                </div>
                                <div>
                                    <div className="font-mono text-xl sm:text-2xl font-bold text-[#FFD700] stat-number delay-8">{stats?.unique_users || 0}</div>
                                    <div className="text-white/40 text-xs sm:text-sm">{t('home.users')}</div>
                                </div>
                            </div>
                        </div>

                        {/* Right - Logo/Visual */}
                        <div className="hidden lg:flex justify-center items-center animate-scale-in delay-2">
                            <div className="relative">
                                <div className="absolute inset-0 bg-[#00FF66]/10 blur-[100px] rounded-full" />
                                <img 
                                    src={BET4PEER_LOGO}
                                    alt="Bet4Peer"
                                    className="relative w-96 h-96 object-cover rounded-3xl border border-white/10 shadow-2xl hover:scale-[1.02] transition-transform duration-500"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <TokenomicsWidget />
            <section className="py-20 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16 animate-fade-in-up">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                            {t('home.howItWorks')} <span className="text-[#00FF66]">{t('home.works')}</span>
                        </h2>
                        <p className="text-white/50 max-w-2xl mx-auto">
                            {t('home.howItWorksDesc')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {steps.map((step, index) => (
                            <div 
                                key={step.number}
                                className={`relative p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-[#00FF66]/20 transition-all group card-hover animate-fade-in-up delay-${index + 1}`}
                            >
                                <div className="text-[#00FF66]/20 font-mono text-5xl font-bold mb-4 group-hover:text-[#00FF66]/30 transition-colors">
                                    {step.number}
                                </div>
                                <h3 className="font-semibold text-lg text-white mb-2">
                                    {step.title}
                                </h3>
                                <p className="text-white/50 text-sm">
                                    {step.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="py-20 bg-gradient-to-b from-transparent via-[#9B6FE8]/5 to-transparent">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16 animate-fade-in-up">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                            {t('home.whyTitle')} <span className="text-[#00FF66]">Bet4Peer</span>
                        </h2>
                        <p className="text-white/50 max-w-2xl mx-auto">
                            {t('home.whyDesc')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <div 
                                    key={feature.title}
                                    className={`p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-[#9B6FE8]/30 transition-all group card-hover animate-fade-in-up delay-${index + 1}`}
                                >
                                    <div className="w-12 h-12 rounded-xl bg-[#9B6FE8]/20 flex items-center justify-center mb-4 group-hover:bg-[#9B6FE8]/30 transition-colors">
                                        <Icon className="w-6 h-6 text-[#00FF66]" />
                                    </div>
                                    <h3 className="font-semibold text-xl text-white mb-2">
                                        {feature.title}
                                    </h3>
                                    <p className="text-white/50">
                                        {feature.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Featured Bets */}
            <section className="py-20 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between mb-12 animate-fade-in-up">
                        <div>
                            <h2 className="text-3xl font-bold mb-2 text-white">
                                <span className="text-[#00FF66]">{t('home.liveBets')}</span> {t('home.bets')}
                            </h2>
                            <p className="text-white/50">
                                {t('home.liveBetsDesc')}
                            </p>
                        </div>
                        <Link to="/bets">
                            <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 hidden md:flex btn-press">
                                {t('home.viewAll')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {Array(3).fill(0).map((_, i) => (
                                <div key={i} className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-4">
                                    <Skeleton className="h-6 w-3/4 bg-white/10" />
                                    <Skeleton className="h-20 w-full bg-white/10" />
                                    <Skeleton className="h-10 w-full bg-white/10" />
                                </div>
                            ))}
                        </div>
                    ) : featuredBets.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {featuredBets.map((bet) => (
                                <BetCard key={bet.id} bet={bet} userAddress={address} />
                            ))}
                        </div>
                    ) : (
                        <div className="p-12 rounded-2xl bg-white/[0.02] border border-white/5 text-center">
                            <div className="w-16 h-16 rounded-full bg-[#00FF66]/10 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-[#00FF66]" />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">{t('home.noBets')}</h3>
                            <p className="text-white/50 mb-6">{t('home.noBetsDesc')}</p>
                            <Link to="/create">
                                <Button className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]">
                                    {t('home.createFirst')}
                                </Button>
                            </Link>
                        </div>
                    )}

                    <div className="mt-8 text-center md:hidden">
                        <Link to="/bets">
                            <Button variant="outline" className="border-white/20 text-white">
                                {t('home.viewAllBets')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Sponsor CTA Section */}
            <section className="py-16 border-t border-white/5" data-testid="sponsor-cta-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FFD700]/10 via-[#9B6FE8]/5 to-[#00FF66]/10 border border-[#FFD700]/20 p-8 md:p-12 animate-fade-in-up">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFD700]/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#9B6FE8]/5 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
                        
                        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                            <div>
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/30 mb-4">
                                    <Flame className="w-3.5 h-3.5 text-[#FFD700]" />
                                    <span className="text-[#FFD700] text-xs font-semibold">
                                        {sponsorStats?.early_bird_remaining > 0
                                            ? t('home.earlyBirdSpots', { count: sponsorStats.early_bird_remaining })
                                            : t('home.sponsorProgram')}
                                    </span>
                                </div>
                                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                    {t('home.earnEth')} <span className="text-[#FFD700]">ETH</span> {t('home.byReferring')}
                                </h2>
                                <p className="text-white/50 mb-6 max-w-md">
                                    {t('home.sponsorDesc')}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <Link to="/sponsor">
                                        <Button className="bg-[#FFD700] text-[#0A0A0C] hover:bg-[#FFE44D] font-semibold px-6 btn-press" data-testid="home-sponsor-cta" aria-label="Become a sponsor and earn ETH">
                                            <Crown className="w-4 h-4 mr-2" />
                                            {t('home.becomeSponsor')}
                                        </Button>
                                    </Link>
                                    <Link to="/sponsor">
                                        <Button variant="outline" className="border-white/10 text-white btn-press">
                                            {t('home.learnMore')}
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </Button>
                                    </Link>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/5 text-center">
                                    <Zap className="w-5 h-5 text-[#9B6FE8] mx-auto mb-2" />
                                    <div className="text-lg font-bold text-white">$100</div>
                                    <div className="text-[10px] text-white/40">{t('home.tierStarter')}</div>
                                    <div className="text-xs text-[#9B6FE8] font-semibold mt-1">+10 B4P</div>
                                </div>
                                <div className="p-4 rounded-2xl bg-white/[0.03] border border-[#00FF66]/20 text-center ring-1 ring-[#00FF66]/10">
                                    <Zap className="w-5 h-5 text-[#00FF66] mx-auto mb-2" />
                                    <div className="text-lg font-bold text-white">$200</div>
                                    <div className="text-[10px] text-white/40">{t('home.tierPro')}</div>
                                    <div className="text-xs text-[#00FF66] font-semibold mt-1">+20 B4P</div>
                                </div>
                                <div className="p-4 rounded-2xl bg-white/[0.03] border border-[#FFD700]/20 text-center ring-1 ring-[#FFD700]/10">
                                    <Crown className="w-5 h-5 text-[#FFD700] mx-auto mb-2" />
                                    <div className="text-lg font-bold text-white">$300</div>
                                    <div className="text-[10px] text-white/40">{t('home.tierElite')}</div>
                                    <div className="text-xs text-[#FFD700] font-semibold mt-1">+50 B4P</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ROI Calculator — viral conversion widget for B4P + Mythic combo */}
            <RoiCalculator />

            {/* CTA Section */}
            <section className="py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-scale-in">
                    <div className="p-12 rounded-3xl bg-gradient-to-br from-[#9B6FE8]/15 to-[#00FF66]/10 border border-white/10 animate-border-glow">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                            {t('home.readyTo')} <span className="text-[#00FF66]">{t('home.bet')}</span>?
                        </h2>
                        <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
                            {t('home.readyDesc')}
                        </p>
                        {isConnected ? (
                            <Link to="/create">
                                <Button 
                                    size="lg"
                                    className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] hover:shadow-[0_0_30px_rgba(130,255,122,0.4)] font-semibold px-12 py-6 text-lg btn-press"
                                >
                                    {t('home.createYourBet')}
                                </Button>
                            </Link>
                        ) : (
                            <Button 
                                size="lg"
                                onClick={connect}
                                className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] hover:shadow-[0_0_30px_rgba(130,255,122,0.4)] font-semibold px-12 py-6 text-lg btn-press"
                                data-testid="cta-connect-wallet"
                                aria-label="Connect your crypto wallet to start betting"
                            >
                                <Wallet className="w-5 h-5 mr-2" />
                                {t('common.connectWallet')}
                            </Button>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
